import React, {useEffect, useRef, useState} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Linking,
  ActivityIndicator
} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {LOT_METADATA_REPORT_CLEAR} from '../../constants';

const REPORT_TYPES = [
  {value: 'lot', label: 'Lot/Batch ID + SKU', fieldLabel: 'Lot/Batch ID'},
  {value: 'serial', label: 'Serial + SKU', fieldLabel: 'Serial'},
  {value: 'exp_date', label: 'Exp Date + SKU', fieldLabel: 'Expiration Date'},
  {value: 'mfg_date', label: 'Mfc Date + SKU', fieldLabel: 'Manufacture Date'},
  {value: 'bestbuy_date', label: 'BestBy Date + SKU', fieldLabel: 'Best By Date'}
];

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const newRequestId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const LotMetadataReportSection = () => {
  const [expanded, setExpanded] = useState(false);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [reportType, setReportType] = useState('lot');
  const [value, setValue] = useState('');
  const [sku, setSku] = useState('');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [download, setDownload] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [resultCounts, setResultCounts] = useState(null);

  const dispatch = useDispatch();
  const pendingRequestId = useRef(null);
  const lastResult = useSelector(state => state.lotMetadataReport.lastResult);
  const handledReceivedAt = useRef(null);

  useEffect(() => {
    if (!loading || !lastResult) return;
    if (lastResult.request_id && lastResult.request_id !== pendingRequestId.current) return;
    if (handledReceivedAt.current === lastResult.receivedAt) return;
    handledReceivedAt.current = lastResult.receivedAt;

    setLoading(false);
    if (lastResult.status) {
      setReady(true);
      setDownload(lastResult.filename);
      setResultCounts(
        lastResult.row_count == null || lastResult.order_count == null
          ? null
          : {rows: lastResult.row_count, orders: lastResult.order_count}
      );
    } else {
      const message = (lastResult.messages && lastResult.messages.length)
        ? lastResult.messages.join(', ')
        : 'Report generation failed. Please try again.';
      setErrorMessage(message);
    }
  }, [lastResult, loading]);

  const selectedType = REPORT_TYPES.find(type => type.value === reportType) || REPORT_TYPES[0];

  const generateReport = async () => {
    if (!sku || !value) {
      setErrorMessage(`SKU and ${selectedType.fieldLabel} are both required`);
      return;
    }

    setErrorMessage('');
    setReady(false);
    setDownload(null);
    setResultCounts(null);
    setLoading(true);
    const requestId = newRequestId();
    pendingRequestId.current = requestId;
    dispatch({type: LOT_METADATA_REPORT_CLEAR});

    try {
      const [accessToken, apiUrl] = await Promise.all([
        AsyncStorage.getItem('access_token'),
        AsyncStorage.getItem('url')
      ]);
      await axios({
        method: 'get',
        url: `${apiUrl}/settings/lot_metadata_report.json`,
        params: {report_type: reportType, sku, value, request_id: requestId},
        headers: {Authorization: `Bearer ${accessToken}`}
      });
    } catch (error) {
      setLoading(false);
      setErrorMessage('Something went wrong while generating the report. Please try again.');
    }
  };

  const downloadReport = () => {
    if (download?.url) {
      Linking.openURL(download.url);
    }
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity
        testID="lotMetadataReportToggle"
        style={styles.header}
        onPress={() => setExpanded(!expanded)}>
        <Text style={styles.headerText}>Lot &amp; Metadata Order Report</Text>
        <Text style={styles.chevron}>{expanded ? '▾' : '▸'}</Text>
      </TouchableOpacity>

      {expanded && (
        <View style={styles.body}>
          <Text style={styles.label}>Report Type</Text>
          <TouchableOpacity
            testID="lotMetadataReportTypeToggle"
            style={styles.dropdownButton}
            onPress={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}>
            <Text style={styles.dropdownText}>{selectedType.label}</Text>
          </TouchableOpacity>
          {isTypeDropdownOpen && (
            <View style={styles.dropdownContainer}>
              <FlatList
                data={REPORT_TYPES}
                keyExtractor={item => item.value}
                renderItem={({item}) => (
                  <TouchableOpacity
                    testID={`lotMetadataReportType-${item.value}`}
                    style={styles.dropdownItem}
                    onPress={() => {
                      setReportType(item.value);
                      setIsTypeDropdownOpen(false);
                    }}>
                    <Text
                      style={[
                        styles.itemText,
                        item.value === reportType && styles.selectedText
                      ]}>
                      {item.label}
                    </Text>
                  </TouchableOpacity>
                )}
              />
            </View>
          )}

          <Text style={styles.label}>{selectedType.fieldLabel}</Text>
          <TextInput
            testID="lotMetadataReportValue"
            style={styles.input}
            value={value}
            onChangeText={setValue}
          />

          <Text style={styles.label}>SKU</Text>
          <TextInput
            testID="lotMetadataReportSku"
            style={styles.input}
            value={sku}
            onChangeText={setSku}
          />

          {!!errorMessage && <Text style={styles.errorText}>{errorMessage}</Text>}

          <View style={styles.buttonRow}>
            <TouchableOpacity
              testID="lotMetadataReportGenerate"
              style={[styles.button, loading && styles.buttonDisabled]}
              disabled={loading}
              onPress={generateReport}>
              <Text style={styles.buttonText}>Generate</Text>
            </TouchableOpacity>

            <TouchableOpacity
              testID="lotMetadataReportDownload"
              style={[styles.button, !ready && styles.buttonDisabled]}
              disabled={!ready}
              onPress={downloadReport}>
              <Text style={styles.buttonText}>Download</Text>
            </TouchableOpacity>
          </View>

          {!!resultCounts && (
            <Text style={styles.resultText}>
              {`${pluralize(resultCounts.rows, 'row')} in ${pluralize(resultCounts.orders, 'order')}`}
            </Text>
          )}

          {loading && (
            <View style={styles.progressRow}>
              <ActivityIndicator size="small" color="#5AA6D1" />
              <Text style={styles.progressText}>
                Report generation in progress, this may take a few minutes.
              </Text>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 20,
    width: '100%'
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 15,
    backgroundColor: 'rgba(45, 68, 95, 1)',
    borderRadius: 8
  },
  headerText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 600
  },
  chevron: {
    color: 'white',
    fontSize: 14
  },
  body: {
    padding: 15,
    backgroundColor: 'rgba(45, 68, 95, 0.5)',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8
  },
  label: {
    color: 'white',
    fontSize: 13,
    marginTop: 10,
    marginBottom: 4
  },
  dropdownButton: {
    width: 260,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 5,
    padding: 10,
    backgroundColor: '#fff'
  },
  dropdownText: {
    color: '#000'
  },
  dropdownContainer: {
    width: 260,
    backgroundColor: 'rgba(45, 68, 95, 1)',
    borderRadius: 5,
    zIndex: 1
  },
  dropdownItem: {},
  itemText: {
    color: '#fff',
    padding: 10
  },
  selectedText: {
    fontWeight: 'bold',
    backgroundColor: '#4b5f76'
  },
  input: {
    width: 260,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 5,
    padding: 10,
    backgroundColor: '#fff',
    color: '#000'
  },
  errorText: {
    color: '#ff6b6b',
    marginTop: 10
  },
  buttonRow: {
    flexDirection: 'row',
    marginTop: 15
  },
  button: {
    backgroundColor: '#5AA6D1',
    borderRadius: 5,
    paddingVertical: 10,
    paddingHorizontal: 20,
    marginRight: 10
  },
  buttonDisabled: {
    opacity: 0.5
  },
  buttonText: {
    color: 'white',
    fontWeight: 600
  },
  resultText: {
    color: 'white',
    marginTop: 12
  },
  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12
  },
  progressText: {
    color: 'white',
    marginLeft: 8,
    fontStyle: 'italic'
  }
});

export default LotMetadataReportSection;
